import {
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Search,
  X,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { formatDate, MappingStatus, MappingStyles } from "../../utils/utils";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import Pagination from "./common/Pagination";
import ConfirmationDialog from "../StaffPortal/ConfirmationDialog";
import { crmAPI } from "../../services/AdminPortal/crmAPI";
import { notificationAPI } from "../../services/notificationAPI";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import "../../datepicker.css"

const ApplicationTable = ({
  setActiveApplications,
  handleDownloadProfile,
  activeApplications,
  adminId,
  handleViewApplication,
  staff,
  userRole,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [pickerMode, setPickerMode] = useState("date");

  // Function to get the correct navigation path based on user role
  const getCustomersPath = () => {
    const role = user?.role?.toLowerCase() || userRole?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      return `/${role}/customers`;
    } else {
      console.warn("Unknown role:", role);
      return "/admin/customers";
    }
  };
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // State for date range picker
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({});
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown-id]") &&
        !event.target.closest("[data-dropdown-menu]")
      ) {
        setDropdownOpen({});
      }
    };
    const handleScroll = () => {
      setDropdownOpen({});
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };
  // Debug: Log modal state changes
  useEffect(() => {
    console.log("Modal state changed:", isModalOpen);
  }, [isModalOpen]);
  const [applicantToAssign, setApplicantToAssign] = useState(null); // State to hold the applicant being assigned
  const [staffToAssign, setStaffToAssign] = useState(null); // State to hold the staff member being assigned
  const [isAssigning, setIsAssigning] = useState(null); // State to track which applicant is being assigned (stores applicant._id)
  const openAssignmentModal = (applicant, staffMember) => {
    console.log("Opening assignment modal:", { applicant, staffMember });
    setApplicantToAssign(applicant);
    setStaffToAssign(staffMember);
    setIsModalOpen(true);
    console.log("Modal state set to:", true);
  };

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      toast.success(`Notification sent: ${note}`);
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Could not send notification.");
    }
  };
  // Function to handle confirmation
  const handleConfirmAssignment = async () => {
    if (!applicantToAssign || !staffToAssign) return;

    setIsAssigning(applicantToAssign._id);
    try {
      // Call the API to update the backend
      const result = await crmAPI.assignLeadToStaff(
        applicantToAssign._id,
        staffToAssign._id
      );

      // Only send notification if assignment was successful
      try {
        await sendNotification(
          staffToAssign._id,
          "New lead is assigned to you.",
          "Admin",
          user._id
        );
      } catch (notificationError) {
        console.warn("Failed to send notification:", notificationError);
      }

      // Update the local state to reflect the change immediately
      setActiveApplications((prevApplicants) =>
        prevApplicants.map((app) =>
          app._id === applicantToAssign._id
            ? { ...app, assignedTo: staffToAssign }
            : app
        )
      );

      toast.success(
        `Successfully assigned ${
          applicantToAssign.user?.name || "applicant"
        } to ${staffToAssign.name}`
      );
    } catch (error) {
      console.error(
        "Error assigning staff:",
        error.response?.data || error.message
      );
      toast.error(
        `Failed to assign staff: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setIsAssigning(null);
      setLoading(false);
      setIsModalOpen(false);
      setApplicantToAssign(null);
      setStaffToAssign(null);
    }
  };
  const toggleDropdown = (applicantId, event) => {
    const isOpening = !dropdownOpen[applicantId];
    setDropdownOpen({ [applicantId]: isOpening });

    if (isOpening && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 160;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropdownPosition((prev) => ({
        ...prev,
        [applicantId]: {
          top: rect.bottom + 8,
          left: Math.max(8, Math.min(rect.left - 48, window.innerWidth - 152)),
          openUpward:
            spaceBelow < dropdownHeight && spaceAbove > dropdownHeight,
        },
      }));
    }
  };
  const handleStaffSelect = (applicant, staffMember) => {
    console.log("Staff selected:", { applicant, staffMember });
    console.log("Current modal state before opening:", isModalOpen);
    setDropdownOpen({}); // Close dropdown

    // Try direct state update first
    console.log("Setting modal to true directly");
    setIsModalOpen(true);

    // Then call the function
    openAssignmentModal(applicant, staffMember);
  };
  const ITEMS_PER_PAGE = 5;
  const statusOptions = [
    { value: "All", label: "All Statuses" },
    { value: "new-leads", label: "New Leads" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "in-review", label: "In Review" },
  ];
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
  };

  const REQUIRED_DOCUMENTS = {
    salaried: [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "SALARY_SLIP",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    "self-employed": [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "INCOME_TAX_RETURN",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    "business-owner": [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "GST_DOCUMENT",
      "COMPANY_REGISTRATION_LETTER",
      "MSME_UDYAM_LICENSE",
      "MUNICIPAL_LICENSE",
      "RENTAL_AGREEMENT",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    default: [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "SALARY_SLIP",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
  };

  const getDocumentSummary = (documents = [], employmentType = "salaried") => {
    const requiredDocs =
      REQUIRED_DOCUMENTS[employmentType] || REQUIRED_DOCUMENTS.default;

    // Ensure documents is an array
    const docsArray = Array.isArray(documents) ? documents : [];

    const verifiedCount = requiredDocs.filter((docType) => {
      const doc = docsArray.find((d) => d?.documentType === docType);
      return doc?.status === "verified";
    }).length;

    const totalRequired = requiredDocs.length;
    const overallStatus =
      verifiedCount === totalRequired ? "verified" : "pending";

    return { verifiedCount, totalRequired, overallStatus };
  };

  const DocumentStatusBadge = ({ documents, employmentType, status }) => {
    const { verifiedCount, totalRequired } = getDocumentSummary(
      documents,
      employmentType
    );
    const isVerified = verifiedCount === totalRequired;

    const bgColor = isVerified ? "bg-green-900" : "bg-yellow-900";
    const textColor = isVerified ? "text-green-400" : "text-yellow-400";
    const label = isVerified ? "verified" : "pending";

    return (
      <span
        className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${bgColor} ${textColor}`}
      >
        {label} • {verifiedCount}/{totalRequired}
      </span>
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate, selectedStatus]);
  const finalFilteredApplicants = activeApplications.filter((app) => {
    // Safely handle undefined or null values
    const applicantName = app?.user?.name || "";
    const status = app?.status || "";
    const applicationDate = app?.createdAt || "";

    const matchesSearch = applicantName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) || app?.loanType.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === "All" ||
      status.toLowerCase() === selectedStatus.toLowerCase();

    // Date filtering
    let matchesDate = true;
    if (pickerMode === "date") {
      matchesDate =
        (!startDate || new Date(app.date) >= new Date(startDate)) &&
        (!endDate || new Date(app.date) <= new Date(endDate));
    } else if (pickerMode === "month" && startDate) {
      const appDate = new Date(app.date);
      matchesDate =
        appDate.getMonth() === startDate.getMonth() &&
        appDate.getFullYear() === startDate.getFullYear();
    } else if (pickerMode === "year" && startDate) {
      const appDate = new Date(app.date);
      matchesDate = appDate.getFullYear() === startDate.getFullYear();
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleCheckboxChange = (id) => {
    if (selectedApplicants.includes(id)) {
      setSelectedApplicants(selectedApplicants.filter((i) => i !== id));
    } else {
      setSelectedApplicants([...selectedApplicants, id]);
      setSelectedApplicants([...selectedApplicants, id]);
    }
  };

  const handleSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    if (newSelectedAll) {
      setSelectedApplicants(finalFilteredApplicants.map((app) => app._id));
    } else {
      setSelectedApplicants([]);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(finalFilteredApplicants.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentTableData = finalFilteredApplicants.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const exportToExcelAndZip = async () => {
    const applicationsToExport =
      selectedApplicants.length > 0
        ? finalFilteredApplicants.filter((app) =>
            selectedApplicants.includes(app._id)
          )
        : finalFilteredApplicants;

    const wb = XLSX.utils.book_new();

    const dataForExcel = applicationsToExport.map((app) => ({
      "Application ID": app._id,
      "Applicant Name": app.username || "",
      "Loan Amount": app.principle,
      "Loan Type": app.loanType,
      Status: app.status,
      "Created At": formatDate(app.createdAt),
      "Assigned To": app.assignedTo?.name || "Not Assigned",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Applications");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("applications.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "applications.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="text-white">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Active Applications</h3>
        <div className="flex items-center gap-4">
          {/* <button 
            onClick={() => {
              console.log("Test button clicked");
              setIsModalOpen(true);
            }}
            className="bg-red-500 text-white px-3 py-1 rounded text-sm"
          >
            Test Modal
          </button> */}
          <div
            onClick={() => navigate(getCustomersPath())}
            className="flex items-center text-[#FFCA20] cursor-pointer hover:underline"
          >
            Go to All Loan Applications
            <ChevronRight size={18} className="ml-1" />
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 order-1 sm:order-none">
          <div className="relative flex w-full sm:w-auto">
            <Search className="absolute left-3 top-2 pr-2 w-6 h-6 text-white" />
            <input
              type="text"
              placeholder="Search by Applicant Name"
              className="bg-transparent border border-[#333333] rounded-3xl text-white w-full sm:min-w-72 pl-10 pr-3 py-2 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
              <button
                className="flex items-center px-3 py-2 focus:outline-none gap-2"
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              >
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                    fill="#FFCA20"
                  />
                </svg>
                {startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : getPickerLabel()}
              </button>

              {isDatePickerOpen && (
                <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
                  {/* Picker Mode Switch */}
                  <div className="flex gap-2 mb-2">
                    <button
                      className={`px-3 py-1 rounded ${
                        pickerMode === "date"
                          ? "bg-yellow-500 text-black"
                          : "bg-[#333] text-white"
                      }`}
                      onClick={() => setPickerMode("date")}
                    >
                      Date
                    </button>
                    <button
                      className={`px-3 py-1 rounded ${
                        pickerMode === "month"
                          ? "bg-yellow-500 text-black"
                          : "bg-[#333] text-white"
                      }`}
                      onClick={() => setPickerMode("month")}
                    >
                      Month
                    </button>
                    <button
                      className={`px-3 py-1 rounded ${
                        pickerMode === "year"
                          ? "bg-yellow-500 text-black"
                          : "bg-[#333] text-white"
                      }`}
                      onClick={() => setPickerMode("year")}
                    >
                      Year
                    </button>
                  </div>

                  {/* Calendar */}
                  <DatePicker
                    selected={startDate}
                    onChange={(update) => {
                      if (pickerMode === "date") {
                        setDateRange(update);
                        if (update[0] && update[1]) setIsDatePickerOpen(false);
                      } else {
                        setDateRange([update, update]);
                        setIsDatePickerOpen(false);
                      }
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange={pickerMode === "date"}
                    showMonthYearPicker={pickerMode === "month"}
                    showYearPicker={pickerMode === "year"}
                    inline
                  />
                </div>
              )}
            </div>

            <div className="relative bg-transparent  rounded-lg border-2  border-[#333333]">
              <button
                className="flex items-center px-3 py-2 gap-2 focus:outline-none"
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              >
                {selectedStatus === "All" ? "Status" : selectedStatus}
                <svg
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.5938 3.15625L8.84375 8.65625C8.59375 8.90625 8.28125 9 8 9C7.6875 9 7.375 8.90625 7.125 8.65625L1.375 3.15625C0.875 2.6875 0.84375 1.90625 1.34375 1.40625C1.8125 0.90625 2.59375 0.875 3.09375 1.375L8 6.03125L12.875 1.375C13.375 0.875 14.1562 0.90625 14.625 1.40625C15.125 1.90625 15.0938 2.6875 14.5938 3.15625Z"
                    fill="#FFCA20"
                  />
                </svg>
              </button>

              {isStatusDropdownOpen && (
                <div className="absolute z-10 mt-1 overflow-y-scroll w-48 bg-[#1e1e1e] rounded-md shadow-lg scrollbar-hide">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      className="block w-full text-left px-4 py-2 text-sm  text-white hover:bg-[#2c2c2c]"
                      onClick={() => handleStatusChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex order-2 sm:order-none">
          <button
            onClick={exportToExcelAndZip}
            className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold flex items-center gap-2"
          >
            Download as ZIP{" "}
            <svg
              width="15"
              height="21"
              viewBox="0 0 15 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.2578 4.17188C14.7266 4.64062 15 5.26562 15 5.92969V18C15 19.4062 13.8672 20.5 12.5 20.5H2.5C1.09375 20.5 0 19.4062 0 18V3C0 1.63281 1.09375 0.5 2.5 0.5H9.57031C10.2344 0.5 10.8594 0.773438 11.3281 1.24219L14.2578 4.17188ZM13.125 18V6.75H10C9.29688 6.75 8.75 6.20312 8.75 5.5V2.41406H6.875V3H4.375V2.41406H2.5C2.14844 2.41406 1.875 2.6875 1.875 3.03906V18C1.875 18.3516 2.14844 18.625 2.5 18.625H12.5C12.8125 18.625 13.125 18.3516 13.125 18ZM6.875 4.25V5.5H4.375V4.25H6.875ZM6.875 6.75V8H4.375V6.75H6.875ZM6.875 9.25L8.04688 13.8203C8.35938 15.3438 7.1875 16.75 5.625 16.75C4.02344 16.75 2.85156 15.3438 3.16406 13.8203L4.375 9.25H6.875ZM6.25 14.875C6.5625 14.875 6.875 14.6016 6.875 14.25C6.875 13.9375 6.5625 13.625 6.25 13.625H5C4.64844 13.625 4.375 13.9375 4.375 14.25C4.375 14.6016 4.64844 14.875 5 14.875H6.25Z"
                fill="#131313"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="  bg-[#131313] rounded-lg border border-[#333333] w-full  overflow-x-auto scrollbar-hide">
        <table className="min-w-full">
          <thead className="border-y border-[#333333]">
            <tr>
              <th className="px-4 py-3 text-left border-2 border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                APPLICATION ID
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                APPLICANT NAME
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                LOAN AMOUNT
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                STATUS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                DOCUMENTS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                ASSIGNED TO
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {finalFilteredApplicants.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-6 text-center text-gray-400">
                  No values to display now.
                </td>
              </tr>
            ) : (
              currentTableData.map((applicant, index) => (
                <tr key={applicant._id} className="border-b border-[#333]">
                  <td className="px-4 py-3 border-r border-[#333333]">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedApplicants.includes(applicant._id)}
                      onChange={() => handleCheckboxChange(applicant._id)}
                    />
                  </td>
                  <td className="p-4 text-center border-r border-[#333333]">
                    <div>{applicant._id}</div>
                    <div className="text-xs text-gray-400">
                      {formatDate(applicant.createdAt)}
                    </div>
                  </td>
                  <td
                    className={`p-4 text-center border-r border-[#333333] ${
                      applicant.referredBy !== null
                        ? "flex flex-col gap-1 "
                        : ""
                    }`}
                  >
                    {applicant.user?.name || applicant?.username || "N/A"}
                    {applicant.referredBy !== null ? (
                      <span className="text-xs text-gray-400">
                        <span className="text-xs text-[#FFCA20]">
                          Affiliate Application
                        </span>
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="p-4 text-center border-r border-[#333333]">
                    <div>{applicant.principle}</div>
                    <div className="text-xs text-gray-400">
                      {applicant.loanType}
                    </div>
                  </td>
                  <td className="p-2 sm:p-4 text-center border-r border-[#333333] min-w-[100px]">
                    <span
                      className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm whitespace-nowrap ${MappingStyles(
                        applicant.status
                      )}
                        `}
                    >
                      {MappingStatus(applicant.status)}
                    </span>
                  </td>
                  <td className="p-2 sm:p-4 text-center border-r border-[#333333] min-w-[120px]">
                    <DocumentStatusBadge
                      documents={applicant.documents}
                      employmentType={applicant.user?.employmentType}
                      status={applicant.status}
                    />
                  </td>
                  <td className="p-4 text-center border-r border-[#333333]">
                    <div className="flex items-center gap-2">
                      <span
                        className="cursor-pointer"
                        data-dropdown-id={applicant._id}
                        onClick={(e) => toggleDropdown(applicant._id, e)}
                      >
                        {applicant.assignedTo?.name || "Assign to"}
                      </span>

                      {/* Dropdown menu */}
                      {dropdownOpen[applicant._id] && (
                        <div
                          data-dropdown-menu
                          className="fixed z-[9999] w-36 rounded-xl shadow-xl border border-[#333333] bg-[#131313]"
                          style={{
                            top: dropdownPosition[applicant._id]?.openUpward
                              ? "auto"
                              : (dropdownPosition[applicant._id]?.top || 0) +
                                "px",
                            bottom: dropdownPosition[applicant._id]?.openUpward
                              ? window.innerHeight -
                                (dropdownPosition[applicant._id]?.top || 0) +
                                "px"
                              : "auto",
                            left: dropdownPosition[applicant._id]?.left || 0,
                          }}
                        >
                          {loading ? (
                            <div className="p-3 text-sm text-white">
                              Loading...
                            </div>
                          ) : (
                            staff.map((staffMember, idx) => (
                              <div
                                key={idx}
                                className="p-3 text-sm text-white cursor-pointer hover:bg-[#1f1f1f] transition duration-150"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStaffSelect(applicant, staffMember);
                                }}
                              >
                                {staffMember.name}
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      <ChevronDown
                        data-dropdown-id={applicant._id}
                        onClick={(e) => toggleDropdown(applicant._id, e)}
                        size={18}
                        className="text-gray-400 cursor-pointer"
                      />
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownloadProfile(applicant._id)}
                        className="text-[#FFCA20] hover:text-yellow-300"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 14.25C22.6406 14.25 24 15.6094 24 17.25V21C24 22.6875 22.6406 24 21 24H3C1.3125 24 0 22.6875 0 21V17.25C0 15.6094 1.3125 14.25 3 14.25H5.48438L7.73438 16.5H3C2.57812 16.5 2.25 16.875 2.25 17.25V21C2.25 21.4219 2.57812 21.75 3 21.75H21C21.375 21.75 21.75 21.4219 21.75 21V17.25C21.75 16.875 21.375 16.5 21 16.5H16.2188L18.4688 14.25H21ZM20.25 19.125C20.25 19.7812 19.7344 20.25 19.125 20.25C18.4688 20.25 18 19.7812 18 19.125C18 18.5156 18.4688 18 19.125 18C19.7344 18 20.25 18.5156 20.25 19.125ZM11.2031 17.2969L4.82812 10.9219C4.35938 10.5 4.35938 9.79688 4.82812 9.375C5.25 8.90625 5.95312 8.90625 6.375 9.375L10.875 13.8281V1.125C10.875 0.515625 11.3438 0 12 0C12.6094 0 13.125 0.515625 13.125 1.125V13.8281L17.5781 9.375C18 8.90625 18.7031 8.90625 19.125 9.375C19.5938 9.79688 19.5938 10.5 19.125 10.9219L12.75 17.2969C12.5625 17.5312 12.2812 17.625 12 17.625C11.6719 17.625 11.3906 17.5312 11.2031 17.2969Z"
                            fill="#FFCA20"
                          />
                        </svg>
                      </button>
                      <button
                        className="text-[#FFCA20] hover:text-[#FFCA20]"
                        onClick={() => {
                          const role = user?.role?.toLowerCase();
                          if (["admin", "subadmin"].includes(role)) {
                            navigate(
                              `/${role}/loan-applications/approval/${applicant._id}`
                            );
                          } else {
                            console.warn("Unknown role:", role);
                          }
                        }}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 0 && (
        <div className="flex justify-center items-center mt-4 pt-4 border-t border-[#333333]">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}

      <ConfirmationDialog
        isOpen={isModalOpen}
        onClose={() => {
          console.log("Modal closing");
          setIsModalOpen(false);
        }}
        onConfirm={handleConfirmAssignment}
        title="Confirm Staff Assignment"
        message={`Are you sure you want to assign this applicant to ${
          staffToAssign?.name || "selected staff"
        }?`}
      />
    </div>
  );
};

export default ApplicationTable;
